import { Task } from '../shared/types';

export interface ActivityGroup {
  key: string;
  name: string;
  category: string;
  floor: string;
  totalQuantity: number;
  unit: string;
  tasks: Task[];
  taskCount: number;
  overdueCount: number;
  overdueAvgDays: number;
  isReady: boolean;
  isInProgress: boolean;
  isBlocked: boolean;
  completionRate: number;
  estimatedDays: number;
}

export interface ActivitySettings {
  mode: 'sequential' | 'parallel';
  crews: number;
  workersPerCrew: number;
}

export interface CapacityResult {
  sequential: {
    days: number;
    fits: boolean;
    maxUnits: number;
  };
  parallel: {
    days: number;
    fits: boolean;
    crews: number;
  };
}

// Calculate if task is overdue from ORIGINAL MASTER PLAN baseline
export const calculateMasterPlanDelay = (task: Task) => {
  // Completed tasks are not overdue
  if (task.status === 'Completed') return { isOverdue: false, days: 0 };
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Use original schedule finish date as the immutable baseline
  const originalFinish = new Date(task.originalSchedule?.finishDate || task.finishDate);
  originalFinish.setHours(0, 0, 0, 0);
  
  const diffTime = today.getTime() - originalFinish.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return {
    isOverdue: diffDays > 0,
    days: Math.max(0, diffDays) // Only positive delays
  };
};

// Check if task is "Ready to Start" (all dependencies completed)
export const isWorkFrontReady = (task: Task, allTasks: Task[]) => {
  // Completed tasks are not work fronts
  if (task.status === 'Completed') return false;
  
  // If no dependencies, it's an open work front (can be started or continued)
  if (!task.dependencies || task.dependencies.length === 0) return true;
  
  // Check if ALL dependencies are completed
  return task.dependencies.every(depId => {
    const depTask = allTasks.find(t => t.id === depId);
    return depTask && depTask.status === 'Completed';
  });
};

// Group tasks by activity (category + floor)
export const groupTasksByActivity = (tasks: Task[]): ActivityGroup[] => {
  const groups: Record<string, ActivityGroup> = {};
  
  tasks.forEach(task => {
    // Group by category and floor
    const groupKey = `${task.category}|${task.floor}`;
    const groupName = task.floor === 'N/A' 
      ? task.category 
      : `${task.category} - ${task.floor}`;
    
    if (!groups[groupKey]) {
      groups[groupKey] = {
        key: groupKey,
        name: groupName,
        category: task.category,
        floor: task.floor,
        totalQuantity: 0,
        unit: '',
        tasks: [],
        taskCount: 0,
        overdueCount: 0,
        overdueAvgDays: 0,
        isReady: false,
        isInProgress: false,
        isBlocked: false,
        completionRate: 0,
        estimatedDays: 0
      };
    }
    
    // Aggregate quantity
    if (task.quantity) {
      groups[groupKey].totalQuantity += parseFloat(task.quantity as string) || 0;
      // Set unit from first task with unit (assume all tasks in group have same unit)
      if (!groups[groupKey].unit && task.unit) {
        groups[groupKey].unit = task.unit;
      }
    }
    
    groups[groupKey].tasks.push(task);
    groups[groupKey].taskCount++;
  });
  
  // Analyze each group
  Object.values(groups).forEach(group => {
    const { tasks } = group;
    
    // Check overdue from master plan
    let totalOverdueDays = 0;
    tasks.forEach(task => {
      const masterDelay = calculateMasterPlanDelay(task);
      if (masterDelay.isOverdue) {
        group.overdueCount++;
        totalOverdueDays += masterDelay.days;
      }
    });
    group.overdueAvgDays = group.overdueCount > 0 
      ? Math.round(totalOverdueDays / group.overdueCount) 
      : 0;
    
    // Check if ready (all tasks have dependencies met)
    group.isReady = tasks.every(task => isWorkFrontReady(task, tasks));
    
    // Check if in progress
    const inProgressCount = tasks.filter(t => t.status === 'In Progress' || t.status === 'Started').length;
    group.isInProgress = inProgressCount > 0;
    
    // Check if blocked
    group.isBlocked = !group.isReady && !group.isInProgress;
    
    // Calculate completion rate
    const completedCount = tasks.filter(t => t.status === 'Completed').length;
    group.completionRate = Math.round((completedCount / tasks.length) * 100);
    
    // Estimate days (average duration of tasks)
    const totalDuration = tasks.reduce((sum, t) => {
      const duration = t.originalSchedule?.duration || t.duration || 5;
      return sum + parseInt(duration as string);
    }, 0);
    group.estimatedDays = Math.round(totalDuration / tasks.length);
    
    // Round quantity to 2 decimal places
    group.totalQuantity = Math.round(group.totalQuantity * 100) / 100;
  });
  
  return Object.values(groups);
};

// Calculate capacity for an activity
export const calculateCapacity = (
  units: number,
  durationPerUnit: number,
  crews: number,
  availableDays: number
): CapacityResult => {
  const sequential = units * durationPerUnit;
  const parallel = Math.ceil((units / crews) * durationPerUnit);
  
  return {
    sequential: {
      days: sequential,
      fits: sequential <= availableDays,
      maxUnits: Math.floor(availableDays / durationPerUnit)
    },
    parallel: {
      days: parallel,
      fits: parallel <= availableDays,
      crews: crews
    }
  };
};

